import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import EmailIcon from '@mui/icons-material/Email'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import BusinessIcon from '@mui/icons-material/Business'
import PersonIcon from '@mui/icons-material/Person'
import NotesIcon from '@mui/icons-material/Notes'
import EventIcon from '@mui/icons-material/Event'
import TimelineIcon from '@mui/icons-material/Timeline'
import CheckIcon from '@mui/icons-material/Check'
import LinkIcon from '@mui/icons-material/Link'
import CoPresentIcon from '@mui/icons-material/CoPresent'
import PaymentsIcon from '@mui/icons-material/Payments'
import API from '../../services/API'
import { CRMContext } from '../CRM'
import OwnerAvatar from '../kit/OwnerAvatar'
import PageHeader from '../kit/PageHeader'
import SectionCard from '../kit/SectionCard'
import KeyValueList from '../kit/KeyValueList'
import ConfirmDialog from '../kit/ConfirmDialog'
import ActivityTimeline from '../kit/ActivityTimeline'
import { DemoChip, InterestChip, SoftChip, StageChip, TrialledChip } from '../kit/Chips'
import {
  LEGACY_STAGE,
  STAGES,
  TIER_NAMES,
  followUpState,
  formatCurrency,
  formatDate,
  fromNow,
  subscriptionChip,
  tenantUrl,
  tierLabel,
  toDateInput,
} from '../kit/helpers'
import { ContactFields, InterestField, ProfileFields } from './LeadFields'

const pick = (object, keys) =>
  keys.reduce((acc, key) => ({ ...acc, [key]: object?.[key] ?? null }), {})

const CONTACT_KEYS = [
  'organisationName',
  'contactName',
  'contactEmail',
  'contactPhone',
  'source',
  'mailingList',
]
const SUBSCRIPTION_KEYS = [
  'expectedBillingCadence',
  'expectedMonthlyFee',
  'expectedAnnualFee',
  'currency',
]
const PROFILE_KEYS = [
  'staffNumbers',
  'likelyTier',
  'isCQCregistered',
  'providesEventServices',
  'providesPTS',
  'providesCommercialTraining',
  'existingChallenges',
]

// Card that flips between a read-only view and an inline editor. `onSave`
// receives the draft and resolves true when the save succeeded.
const EditableCard = ({ title, icon, lead, keys, onSave, view, edit }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({})
  const [saving, setSaving] = useState(false)

  const start = () => {
    setDraft(pick(lead, keys))
    setEditing(true)
  }
  const save = async () => {
    setSaving(true)
    const ok = await onSave(draft)
    setSaving(false)
    if (ok) setEditing(false)
  }
  const setField = (name, value) => setDraft((prev) => ({ ...prev, [name]: value }))

  return (
    <SectionCard
      title={title}
      icon={icon}
      action={
        editing ? (
          <>
            <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={save} disabled={saving}>
              Save
            </Button>
          </>
        ) : (
          <Button size="small" startIcon={<EditIcon />} onClick={start}>
            Edit
          </Button>
        )
      }
    >
      {editing ? edit(draft, setField) : view}
    </SectionCard>
  )
}

const StageStepper = ({ stage, onChange, disabled }) => {
  const current = stage ?? 'New'
  const steps = STAGES.filter((option) => option.key !== 'Lost')
  if (current === LEGACY_STAGE.key) {
    steps.splice(3, 0, LEGACY_STAGE)
  }
  const currentIndex = steps.findIndex((option) => option.key === current)

  if (current === 'Lost') {
    return (
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <StageChip stage="Lost" size="medium" />
        <Button size="small" variant="outlined" disabled={disabled} onClick={() => onChange('Contacted')}>
          Reopen as Contacted
        </Button>
      </Stack>
    )
  }

  return (
    <Stack direction="row" gap={0.75} flexWrap="wrap">
      {steps.map((option, index) => {
        const done = index < currentIndex
        const active = index === currentIndex
        return (
          <Tooltip key={option.key} title={active ? 'Current stage' : `Move to ${option.label}`}>
            <span>
              <Button
                size="small"
                disabled={disabled || active}
                onClick={() => onChange(option.key)}
                startIcon={done ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                sx={{
                  borderRadius: 5,
                  px: 1.5,
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: active || done ? option.color : '#E2E8F0',
                  bgcolor: active ? option.color : done ? alpha(option.color, 0.1) : '#FFFFFF',
                  color: active ? '#FFF' : done ? option.color : 'text.secondary',
                  '&.Mui-disabled': {
                    bgcolor: active ? option.color : undefined,
                    color: active ? '#FFF' : undefined,
                    borderColor: active ? option.color : undefined,
                  },
                  '&:hover': { bgcolor: active ? option.color : alpha(option.color, 0.14) },
                }}
              >
                {option.label}
              </Button>
            </span>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

const yesNo = (value) => (value === true ? 'Yes' : value === false ? 'No' : '')

const LeadDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useContext(CRMContext)
  const [lead, setLead] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [confirm, setConfirm] = useState(null) // 'lost' | 'delete' | 'deleteDatabase'
  const [tenantOptions, setTenantOptions] = useState(null)
  const [linkTenant, setLinkTenant] = useState(null)
  const [ownerAnchor, setOwnerAnchor] = useState(null)
  const [owners, setOwners] = useState(null)

  const openOwnerMenu = (event) => {
    setOwnerAnchor(event.currentTarget)
    if (owners) return
    API.get('/admincrm/sales/owners').then((res) => {
      if (res.status === 200) setOwners(res.data)
      else setError(res.data?.message || 'Could not load CRM users.')
    })
  }

  const assignOwner = async (ownerId) => {
    setOwnerAnchor(null)
    await save({ owner: ownerId })
  }

  const load = () => {
    setLoading(true)
    API.get(`/admincrm/sales/leads/${id}`)
      .then((res) => {
        if (res.status === 200) setLead(res.data)
        else setError(res.data?.message || 'Could not load this lead.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(load, [id])

  const save = async (fields) => {
    setBusy(true)
    setError('')
    const res = await API.put(`/admincrm/sales/leads/${id}`, fields)
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not save the changes.')
      return false
    }
    setLead(res.data)
    return true
  }

  const changeStage = (stage) => {
    if (stage === 'Lost') {
      setConfirm('lost')
      return
    }
    save({ stage })
  }

  const markLost = async () => {
    if (!lead.tenantLinked?._id) {
      const ok = await save({ stage: 'Lost' })
      if (ok) setConfirm(null)
      return
    }
    setBusy(true)
    setError('')
    const res = await API.post('/admincrm/sales/tenant-lost', { tenantId: lead.tenantLinked._id })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not mark this lead as lost.')
      return
    }
    setLead(res.data.lead)
    setConfirm(null)
  }

  const logActivity = async ({ kind, note }) => {
    const res = await API.post(`/admincrm/sales/leads/${id}/activity`, { kind, note })
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not record the activity.')
      throw new Error('activity failed')
    }
    setLead(res.data)
  }

  const setFollowUp = (value) =>
    save({ nextFollowUpAt: value ? moment(value).endOf('day').toISOString() : null })

  // The server lifts a New / Contacted lead to Engaged when a demo is
  // recorded, so the stage stepper updates from the response as well.
  const setDemo = (value) =>
    save({ demoAt: value ? moment(value).startOf('day').add(12, 'hours').toISOString() : null })

  const archive = async (archived) => {
    setBusy(true)
    const res = await API.post(`/admincrm/sales/leads/${id}/archive`, { archived })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not update the lead.')
      return
    }
    if (archived) navigate('/sales')
    else load()
  }

  const hardDelete = async () => {
    setBusy(true)
    const res = await API.delete(`/admincrm/sales/leads/${id}`)
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not delete the lead.')
      return
    }
    navigate('/sales')
  }

  const [confirmation, setConfirmation] = useState('')
  const deleteDatabase = async () => {
    setBusy(true)
    setError('')
    const res = await API.post('/admincrm/sales/delete-tenant-customer', {
      tenantId: lead.tenantLinked._id,
      deleteLead: false,
      confirmation,
    })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not delete the tenant database.')
      return
    }
    setConfirm(null)
    load()
  }

  const loadTenantOptions = () => {
    if (tenantOptions) return
    API.get('/admincrm/sales/tenant-options').then((res) => {
      if (res.status === 200) setTenantOptions(res.data)
    })
  }

  if (loading && !lead) return <LinearProgress />
  if (!lead) {
    return (
      <Stack gap={2}>
        <PageHeader back="/sales" eyebrow="Sales lead" title="Lead not found" />
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    )
  }

  const tenant = lead.tenantLinked
  const snapshot = lead.tenantSnapshot
  const followUp = followUpState(lead.nextFollowUpAt)
  const mailto = lead.contactEmail
    ? `mailto:${lead.contactEmail}?subject=${encodeURIComponent(`AmbuNet - ${lead.organisationName}`)}`
    : null

  const services = [
    lead.providesEventServices && 'Event medical',
    lead.providesPTS && 'Patient transport',
    lead.providesCommercialTraining && 'Commercial training',
  ].filter(Boolean)

  return (
    <Stack gap={2.5}>
      {(loading || busy) && <LinearProgress sx={{ borderRadius: 1 }} />}
      <PageHeader
        back="/sales"
        eyebrow="Sales lead"
        title={lead.organisationName}
        actions={
          <>
            <Button variant="outlined" startIcon={<EmailIcon />} href={mailto ?? undefined} disabled={!mailto}>
              Email
            </Button>
            {tenant?.subdomain && (
              <Button
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                onClick={() => window.open(tenantUrl(tenant.subdomain), '_blank')}
              >
                Visit site
              </Button>
            )}
            {tenant?._id && (
              <Button variant="contained" onClick={() => navigate(`/tenants/${tenant._id}`)}>
                Tenant record
              </Button>
            )}
            <IconButton
              aria-label="More actions"
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              sx={{ bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        }
      >
        <Stack direction="row" gap={0.75} flexWrap="wrap" marginTop={1} alignItems="center">
          <Tooltip title="Change owner">
            <Chip
              size="small"
              onClick={openOwnerMenu}
              disabled={busy}
              avatar={<OwnerAvatar owner={lead.owner} size={20} />}
              label={
                lead.owner
                  ? `Owner: ${lead.owner.name}${String(lead.owner._id) === String(user?._id) ? ' (you)' : ''}`
                  : 'Unassigned'
              }
              sx={{ borderRadius: 1.5, fontWeight: 700, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}
            />
          </Tooltip>
          <StageChip stage={lead.stage} />
          {lead.demoAt && <DemoChip demoAt={lead.demoAt} />}
          {lead.trialled && <TrialledChip trialEnd={tenant?.trialEnd ?? snapshot?.trialEnd} />}
          {lead.archived && <SoftChip color="#64748B" label="Archived" />}
          {tenant && (
            <SoftChip
              color={tenant.isActive === false ? '#E32525' : tenant.activeSubscription ? '#00964C' : '#557A95'}
              icon={<BusinessIcon />}
              label={
                tenant.isActive === false
                  ? 'Tenant disabled'
                  : tenant.activeSubscription
                    ? `Customer · ${tierLabel(tenant.subscriptionTier)}`
                    : 'Tenant · not subscribed'
              }
            />
          )}
          {!tenant && snapshot?.databaseDeletedAt && (
            <SoftChip color="#64748B" label={`Database deleted ${formatDate(snapshot.databaseDeletedAt)}`} />
          )}
          {lead.source && <SoftChip color="#64748B" label={lead.source} />}
          <InterestChip level={lead.interestLevel} />
        </Stack>
      </PageHeader>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <SectionCard>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={700} sx={{ minWidth: 60 }}>
            Stage
          </Typography>
          <StageStepper stage={lead.stage} onChange={changeStage} disabled={busy} />
          <Box flex={1} />
          {lead.stage !== 'Lost' && (
            <Button size="small" color="error" onClick={() => setConfirm('lost')} disabled={busy}>
              Mark lost
            </Button>
          )}
        </Stack>
      </SectionCard>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Stack gap={2}>
            <EditableCard
              title="Contact"
              icon={<PersonIcon />}
              lead={lead}
              keys={CONTACT_KEYS}
              onSave={save}
              view={
                <KeyValueList
                  rows={[
                    { label: 'Contact name', value: lead.contactName },
                    {
                      label: 'Email',
                      value: lead.contactEmail,
                      render: lead.contactEmail ? (
                        <Link href={mailto} variant="body2" fontWeight={500}>
                          {lead.contactEmail}
                        </Link>
                      ) : null,
                    },
                    {
                      label: 'Phone',
                      value: lead.contactPhone,
                      render: lead.contactPhone ? (
                        <Link href={`tel:${lead.contactPhone}`} variant="body2" fontWeight={500}>
                          {lead.contactPhone}
                        </Link>
                      ) : null,
                    },
                    { label: 'Source', value: lead.source },
                    { label: 'Mailing list', value: lead.mailingList ? 'Subscribed' : 'Not subscribed' },
                    {
                      label: 'Added',
                      value: `${formatDate(lead.createdAt)} (${fromNow(lead.createdAt)})${
                        lead.createdBy ? ` by ${lead.createdBy.name}` : ''
                      }`,
                    },
                  ]}
                />
              }
              edit={(draft, setField) => <ContactFields form={draft} setField={setField} autoFocus />}
            />

            <EditableCard
              title="Organisation profile"
              icon={<BusinessIcon />}
              lead={lead}
              keys={PROFILE_KEYS}
              onSave={(draft) => save({ ...draft, likelyTier: draft.likelyTier === '' ? null : draft.likelyTier })}
              view={
                <KeyValueList
                  emptyText="Nothing captured yet - use Edit to record staff numbers, services and the likely plan."
                  rows={[
                    { label: 'Staff numbers', value: lead.staffNumbers },
                    { label: 'Likely plan', value: TIER_NAMES[Number(lead.likelyTier)] },
                    { label: 'CQC registered', value: yesNo(lead.isCQCregistered) },
                    {
                      label: 'Services',
                      value: services.length ? services.join(', ') : '',
                      render: services.length ? (
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {services.map((service) => (
                            <Chip key={service} label={service} size="small" sx={{ borderRadius: 1.5 }} />
                          ))}
                        </Stack>
                      ) : null,
                    },
                    { label: 'Existing challenges', value: lead.existingChallenges, fullWidth: true },
                  ]}
                />
              }
              edit={(draft, setField) => <ProfileFields form={draft} setField={setField} />}
            />

            <EditableCard
              title="Notes"
              icon={<NotesIcon />}
              lead={lead}
              keys={['notes']}
              onSave={save}
              view={
                lead.notes ? (
                  <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {lead.notes}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No notes yet.
                  </Typography>
                )
              }
              edit={(draft, setField) => (
                <TextField
                  value={draft.notes ?? ''}
                  onChange={(event) => setField('notes', event.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                  size="small"
                  autoFocus
                />
              )}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack gap={2}>
            <SectionCard title="Follow-up" icon={<EventIcon />}>
              <Stack gap={2}>
                <Box>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <TextField
                      label="Next follow-up"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={toDateInput(lead.nextFollowUpAt)}
                      onChange={(event) => setFollowUp(event.target.value)}
                      disabled={busy}
                      sx={{ minWidth: 180 }}
                    />
                    {followUp && (
                      <Typography variant="body2" fontWeight={700} sx={{ color: followUp.color }}>
                        {followUp.key === 'upcoming' ? fromNow(lead.nextFollowUpAt) : followUp.label}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" gap={0.5} flexWrap="wrap" marginTop={1}>
                    {[
                      { label: 'Tomorrow', value: moment().add(1, 'day') },
                      { label: '1 week', value: moment().add(1, 'week') },
                      { label: '2 weeks', value: moment().add(2, 'weeks') },
                      { label: '1 month', value: moment().add(1, 'month') },
                    ].map((option) => (
                      <Chip
                        key={option.label}
                        label={option.label}
                        size="small"
                        clickable
                        disabled={busy}
                        onClick={() => setFollowUp(option.value)}
                        sx={{ borderRadius: 1.5 }}
                      />
                    ))}
                    {lead.nextFollowUpAt && (
                      <Chip
                        label="Clear"
                        size="small"
                        clickable
                        variant="outlined"
                        disabled={busy}
                        onClick={() => setFollowUp(null)}
                        sx={{ borderRadius: 1.5 }}
                      />
                    )}
                  </Stack>
                </Box>
                <Divider />
                <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      LAST CONTACTED
                    </Typography>
                    <Typography variant="body2" color={lead.lastContactedAt ? 'text.primary' : 'text.disabled'}>
                      {lead.lastContactedAt
                        ? `${formatDate(lead.lastContactedAt)} · ${fromNow(lead.lastContactedAt)}`
                        : 'Never'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busy}
                    onClick={() => save({ lastContactedAt: new Date().toISOString() })}
                  >
                    Contacted today
                  </Button>
                </Stack>
                <Divider />
                <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      DEMO
                    </Typography>
                    {lead.demoAt ? (
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" color="text.primary">
                          {`Given ${formatDate(lead.demoAt)} · ${fromNow(lead.demoAt)}`}
                        </Typography>
                        <TextField
                          type="date"
                          size="small"
                          value={toDateInput(lead.demoAt)}
                          onChange={(event) => event.target.value && setDemo(event.target.value)}
                          disabled={busy}
                          inputProps={{ 'aria-label': 'Demo date' }}
                          sx={{ width: 150 }}
                        />
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        Not yet
                      </Typography>
                    )}
                  </Box>
                  {lead.demoAt ? (
                    <Button size="small" disabled={busy} onClick={() => setDemo(null)}>
                      Clear
                    </Button>
                  ) : (
                    <Tooltip title="Records the demo as today and moves a New or Contacted lead to Engaged">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CoPresentIcon />}
                          disabled={busy}
                          onClick={() => setDemo(new Date())}
                        >
                          Demo given
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
                <Divider />
                <InterestField
                  value={lead.interestLevel}
                  onChange={(value) => value && save({ interestLevel: value })}
                />
              </Stack>
            </SectionCard>

            <EditableCard
              title="Subscription forecast"
              icon={<PaymentsIcon />}
              lead={lead}
              keys={SUBSCRIPTION_KEYS}
              onSave={save}
              view={
                <KeyValueList
                  emptyText="No expected subscription yet - use Edit to set the fee this lead would pay. The revenue Forecast page projects from these figures."
                  rows={[
                    {
                      label: 'Billing cadence',
                      value:
                        lead.expectedBillingCadence === 'annually'
                          ? 'Annually'
                          : lead.expectedBillingCadence === 'monthly'
                            ? 'Monthly'
                            : '',
                    },
                    {
                      label: 'Monthly fee',
                      value: formatCurrency(lead.expectedMonthlyFee, lead.currency),
                    },
                    {
                      label: 'Annual fee',
                      value: formatCurrency(lead.expectedAnnualFee, lead.currency),
                    },
                    {
                      label: 'Currency',
                      value: lead.currency && lead.currency !== 'GBP' ? lead.currency : '',
                    },
                  ]}
                />
              }
              edit={(draft, setField) => (
                <Stack gap={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="expected-cadence-label">Billing cadence</InputLabel>
                    <Select
                      labelId="expected-cadence-label"
                      label="Billing cadence"
                      value={draft.expectedBillingCadence ?? ''}
                      onChange={(event) => setField('expectedBillingCadence', event.target.value)}
                    >
                      <MenuItem value="">Not set</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="annually">Annually</MenuItem>
                    </Select>
                  </FormControl>
                  <Stack direction="row" gap={1.5}>
                    <TextField
                      label="Monthly fee"
                      type="number"
                      size="small"
                      fullWidth
                      value={draft.expectedMonthlyFee ?? ''}
                      onChange={(event) => setField('expectedMonthlyFee', event.target.value)}
                      inputProps={{ min: 0 }}
                    />
                    <TextField
                      label="Annual fee"
                      type="number"
                      size="small"
                      fullWidth
                      value={draft.expectedAnnualFee ?? ''}
                      onChange={(event) => setField('expectedAnnualFee', event.target.value)}
                      inputProps={{ min: 0 }}
                    />
                  </Stack>
                  <TextField
                    label="Currency"
                    size="small"
                    value={draft.currency ?? 'GBP'}
                    onChange={(event) => setField('currency', event.target.value.toUpperCase())}
                    inputProps={{ maxLength: 3 }}
                    helperText="ISO code, e.g. GBP or EUR - EU and global leads keep their own currency"
                    sx={{ maxWidth: 200 }}
                  />
                </Stack>
              )}
            />

            <SectionCard
              title={tenant ? 'Tenant' : snapshot ? 'Tenant history' : 'Tenant'}
              icon={<BusinessIcon />}
              action={
                tenant?._id ? (
                  <Button size="small" onClick={() => navigate(`/tenants/${tenant._id}`)}>
                    Open record
                  </Button>
                ) : null
              }
            >
              {tenant ? (
                <Stack gap={1.5}>
                  <Stack direction="row" gap={0.75} flexWrap="wrap">
                    {(() => {
                      const chip = subscriptionChip(tenant)
                      return (
                        <Chip
                          label={chip.label}
                          color={chip.color}
                          variant={chip.variant ?? 'filled'}
                          size="small"
                          sx={{ borderRadius: 1.5 }}
                        />
                      )
                    })()}
                    {tenant.subscriptionTier ? (
                      <Chip label={tierLabel(tenant.subscriptionTier)} size="small" sx={{ borderRadius: 1.5 }} />
                    ) : null}
                  </Stack>
                  <KeyValueList
                    rows={[
                      { label: 'Name', value: tenant.name },
                      {
                        label: 'Site',
                        value: tenant.subdomain,
                        render: tenant.subdomain ? (
                          <Link href={tenantUrl(tenant.subdomain)} target="_blank" rel="noreferrer" variant="body2">
                            {tenant.subdomain}.{process.env.ROOT_DOMAIN}
                          </Link>
                        ) : null,
                      },
                      { label: 'Signed up', value: formatDate(tenant.createdAt) },
                      { label: 'Trial ended', value: formatDate(tenant.trialEnd) },
                      { label: 'Stripe status', value: tenant.stripeSubscriptionStatus },
                      { label: 'Renews', value: formatDate(tenant.stripeSubscriptionExpiry) },
                      {
                        label: 'Why not active',
                        value: tenant.activeSubscription ? '' : tenant.inactiveReason,
                      },
                    ]}
                  />
                  {lead.stage === 'Lost' && (
                    <Alert
                      severity="warning"
                      action={
                        <Button color="inherit" size="small" onClick={() => { setConfirmation(''); setConfirm('deleteDatabase') }}>
                          Delete database
                        </Button>
                      }
                    >
                      This lead is lost. Their tenant is disabled; the database can now be deleted.
                    </Alert>
                  )}
                </Stack>
              ) : snapshot ? (
                <Stack gap={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    {snapshot.databaseDeletedAt
                      ? `Database deleted ${formatDate(snapshot.databaseDeletedAt)}. These figures are all that remain.`
                      : `Captured ${formatDate(snapshot.capturedAt)}.`}
                  </Typography>
                  <KeyValueList
                    rows={[
                      { label: 'Tenant', value: snapshot.name },
                      { label: 'Subdomain', value: snapshot.subdomain },
                      { label: 'Signed up', value: formatDate(snapshot.tenantCreatedAt) },
                      { label: 'Trial ended', value: formatDate(snapshot.trialEnd) },
                      { label: 'Users', value: snapshot.userCount },
                      {
                        label: 'Last user activity',
                        value: snapshot.lastUserActivity ? formatDate(snapshot.lastUserActivity) : 'Never',
                      },
                      { label: 'Plan at the time', value: tierLabel(snapshot.subscriptionTier, '') },
                      { label: 'Stripe status', value: snapshot.stripeSubscriptionStatus },
                    ]}
                  />
                </Stack>
              ) : (
                <Stack gap={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    No tenant is linked. If this organisation already has an AmbuNet account, link
                    it so their subscription and usage show here.
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={tenantOptions ?? []}
                      loading={tenantOptions === null}
                      onOpen={loadTenantOptions}
                      value={linkTenant}
                      onChange={(event, value) => setLinkTenant(value)}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      getOptionLabel={(option) => `${option.name} (${option.subdomain})`}
                      renderInput={(params) => <TextField {...params} label="Link a tenant" />}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      disabled={!linkTenant || busy}
                      onClick={async () => {
                        const ok = await save({ tenantLinked: linkTenant._id })
                        if (ok) setLinkTenant(null)
                      }}
                    >
                      Link
                    </Button>
                  </Stack>
                </Stack>
              )}
            </SectionCard>

            <SectionCard title="Activity" icon={<TimelineIcon />} subtitle="Notes, calls, emails and meetings">
              <ActivityTimeline items={lead.comments} onSubmit={logActivity} />
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      <Menu anchorEl={ownerAnchor} open={!!ownerAnchor} onClose={() => setOwnerAnchor(null)}>
        {!owners && <MenuItem disabled>Loading…</MenuItem>}
        {owners?.map((option) => (
          <MenuItem
            key={option._id}
            selected={String(option._id) === String(lead.owner?._id)}
            onClick={() => assignOwner(option._id)}
          >
            <OwnerAvatar owner={option} showName isMe={String(option._id) === String(user?._id)} />
          </MenuItem>
        ))}
        {owners && lead.owner && <Divider />}
        {owners && lead.owner && <MenuItem onClick={() => assignOwner(null)}>Unassign</MenuItem>}
      </Menu>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {lead.stage !== 'Lost' && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null)
              setConfirm('lost')
            }}
          >
            Mark lost{tenant ? ' & disable tenant' : ''}
          </MenuItem>
        )}
        {!tenant && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null)
              archive(!lead.archived)
            }}
          >
            {lead.archived ? 'Restore to pipeline' : 'Archive lead'}
          </MenuItem>
        )}
        {tenant && lead.stage === 'Lost' && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setMenuAnchor(null)
              setConfirmation('')
              setConfirm('deleteDatabase')
            }}
          >
            Delete tenant database
          </MenuItem>
        )}
        {!tenant && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setMenuAnchor(null)
              setConfirm('delete')
            }}
          >
            Delete permanently
          </MenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={confirm === 'lost'}
        title={`Mark ${lead.organisationName} as lost`}
        confirmLabel={tenant ? 'Mark lost & disable tenant' : 'Mark lost'}
        confirmColor="error"
        busy={busy}
        error={error}
        onClose={() => setConfirm(null)}
        onConfirm={markLost}
      >
        {tenant ? (
          <Alert severity="info">
            {tenant.name} will be disabled so nobody can log in. Nothing is deleted - their data
            stays put and you can reopen the lead at any time to re-enable it. Deleting the
            database is a separate step available once they are lost.
          </Alert>
        ) : (
          <Alert severity="info">This lead has no tenant, so nothing else changes.</Alert>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === 'delete'}
        title={`Delete ${lead.organisationName} permanently`}
        confirmLabel="Delete lead"
        confirmColor="error"
        requireText={lead.organisationName}
        busy={busy}
        error={error}
        onClose={() => setConfirm(null)}
        onConfirm={hardDelete}
      >
        <Alert severity="warning">
          This removes the lead and its whole activity history. If you just want it out of the
          pipeline, archive it instead.
        </Alert>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === 'deleteDatabase'}
        title={`Delete ${tenant?.name}'s database`}
        confirmLabel="Delete database"
        confirmColor="error"
        busy={busy}
        error={error}
        onClose={() => setConfirm(null)}
        onConfirm={deleteDatabase}
      >
        <Alert severity="error">
          This permanently destroys the tenant database and cannot be undone. Everything in their
          account goes: staff records, ePCRs, bookings, documents and audit history.
        </Alert>
        <Alert severity="success">
          Before deleting, their headline figures are copied onto this lead so the sales history
          survives - name, subdomain, plan, trial date, user count and last activity. The lead,
          its notes and its timeline are all kept.
        </Alert>
        <TextField
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          label={`Type "${tenant?.name}" to confirm`}
          size="small"
          fullWidth
          autoComplete="off"
        />
      </ConfirmDialog>
    </Stack>
  )
}

export default LeadDetail
